import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { formatCurrency, formatNumber } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, CurrencyDollar, Users, Check } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Componente Input con formato de miles
const FormattedInput = ({ value, onChange, disabled, className }) => {
  const [isFocused, setIsFocused] = useState(false);
  const [localValue, setLocalValue] = useState(value?.toString() || '');

  useEffect(() => {
    if (!isFocused) {
      setLocalValue(value?.toString() || '');
    }
  }, [value, isFocused]);

  const formatWithCommas = (num) => {
    if (!num && num !== 0) return '';
    return Number(num).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
    const numValue = parseFloat(localValue) || 0;
    onChange(numValue);
  };

  const handleChange = (e) => {
    setLocalValue(e.target.value);
  };

  return (
    <Input
      type={isFocused ? "number" : "text"}
      min="0"
      step="0.01"
      className={className}
      value={isFocused ? localValue : formatWithCommas(value)}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      disabled={disabled}
    />
  );
};

export default function PagoNominaForm() {
  const navigate = useNavigate();
  const [empleados, setEmpleados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [formData, setFormData] = useState({
    fecha_pago: new Date().toISOString().split('T')[0],
    tipo_pago: 'quincenal',
  });
  const [empleadosPago, setEmpleadosPago] = useState({});
  const [selectedAll, setSelectedAll] = useState(true);

  useEffect(() => {
    loadEmpleados();
  }, []);

  const loadEmpleados = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API_BASE}/empleados?activo=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmpleados(response.data);
      
      // Initialize empleados pago with default values from employee fixed deductions
      // Deducciones fijas son mensuales, dividir por 2 si es quincenal
      const initial = {};
      response.data.forEach((emp) => {
        initial[emp.id] = {
          selected: true,
          comisiones: 0,
          // Pre-cargar deducciones fijas del empleado (mensual / 2 para quincenal)
          afp: (emp.afp_fijo || 0) / 2,
          ars: (emp.ars_fijo || 0) / 2,
          cxc: (emp.cxc_fijo || 0) / 2,
          seg_medico: (emp.seg_medico_fijo || 0) / 2,
        };
      });
      setEmpleadosPago(initial);
    } catch (error) {
      toast.error('Error al cargar empleados');
    } finally {
      setLoadingData(false);
    }
  };

  const handleEmpleadoChange = (empId, field, value) => {
    setEmpleadosPago((prev) => ({
      ...prev,
      [empId]: {
        ...prev[empId],
        [field]: field === 'selected' ? value : (typeof value === 'number' ? value : parseFloat(value) || 0),
      },
    }));
  };

  const toggleSelectAll = () => {
    const newSelected = !selectedAll;
    setSelectedAll(newSelected);
    setEmpleadosPago((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((empId) => {
        updated[empId] = { ...updated[empId], selected: newSelected };
      });
      return updated;
    });
  };

  const calculateTotals = () => {
    let totalBruto = 0;
    let totalDeducciones = 0;
    let totalNeto = 0;
    let empleadosSeleccionados = 0;

    empleados.forEach((emp) => {
      const pago = empleadosPago[emp.id];
      if (!pago?.selected) return;

      empleadosSeleccionados++;
      const sueldoBase = formData.tipo_pago === 'quincenal' ? emp.sueldo_quincenal : emp.sueldo_mensual;
      const bruto = sueldoBase + (pago.comisiones || 0);
      const deducciones = (pago.afp || 0) + (pago.ars || 0) + (pago.cxc || 0) + (pago.seg_medico || 0);
      
      totalBruto += bruto;
      totalDeducciones += deducciones;
      totalNeto += bruto - deducciones;
    });

    return { totalBruto, totalDeducciones, totalNeto, empleadosSeleccionados };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const selectedEmpleados = empleados.filter((emp) => empleadosPago[emp.id]?.selected);
    if (selectedEmpleados.length === 0) {
      toast.error('Seleccione al menos un empleado');
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('token');
    
    try {
      const payload = {
        fecha_pago: formData.fecha_pago,  // Enviar como YYYY-MM-DD sin conversión a UTC
        tipo_pago: formData.tipo_pago,
        empleados_pagos: selectedEmpleados.map((emp) => ({
          empleado_id: emp.id,
          comisiones: empleadosPago[emp.id]?.comisiones || 0,
          afp: empleadosPago[emp.id]?.afp || 0,
          ars: empleadosPago[emp.id]?.ars || 0,
          cxc: empleadosPago[emp.id]?.cxc || 0,
          seg_medico: empleadosPago[emp.id]?.seg_medico || 0,
        })),
      };

      await axios.post(`${API_BASE}/nomina/pagos`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Pago de nómina registrado');
      navigate('/nomina');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar pago');
    } finally {
      setLoading(false);
    }
  };

  const totals = calculateTotals();

  if (loadingData) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner h-8 w-8 border-2" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto animate-fade-in" data-testid="pago-form">
        <div className="mb-6">
          <Link
            to="/nomina"
            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-2"
          >
            <ArrowLeft size={14} />
            Volver a Nómina
          </Link>
          <h1 className="text-3xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Generar Pago de Nómina
          </h1>
          <p className="text-muted-foreground">
            Configure las deducciones y comisiones para cada empleado
          </p>
        </div>

        {empleados.length === 0 ? (
          <Card className="border-border/60">
            <CardContent className="py-12">
              <div className="empty-state">
                <Users size={64} className="text-muted-foreground/50 mb-4" />
                <h3 className="text-xl font-semibold mb-2">No hay empleados activos</h3>
                <p className="text-muted-foreground mb-6">
                  Agregue empleados antes de generar un pago
                </p>
                <Link to="/nomina/empleado/nuevo">
                  <Button className="bg-[#E73425] hover:bg-[#D81C23]">
                    Agregar Empleado
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Pago Info */}
            <Card className="border-border/60">
              <CardHeader>
                <CardTitle>Información del Pago</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="fecha_pago">Fecha de Pago</Label>
                    <Input
                      id="fecha_pago"
                      type="date"
                      value={formData.fecha_pago}
                      onChange={(e) => setFormData({ ...formData, fecha_pago: e.target.value })}
                      data-testid="pago-fecha"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tipo_pago">Tipo de Pago</Label>
                    <Select
                      value={formData.tipo_pago}
                      onValueChange={(value) => setFormData({ ...formData, tipo_pago: value })}
                    >
                      <SelectTrigger data-testid="pago-tipo">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="quincenal">Quincenal</SelectItem>
                        <SelectItem value="mensual">Mensual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Empleados Table */}
            <Card className="border-border/60">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Empleados</CardTitle>
                  <CardDescription>
                    Configure comisiones y deducciones para cada empleado
                  </CardDescription>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={toggleSelectAll}
                >
                  {selectedAll ? 'Deseleccionar Todos' : 'Seleccionar Todos'}
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b table-header">
                        <th className="p-3 text-center w-12"></th>
                        <th className="p-3 text-left">Empleado</th>
                        <th className="p-3 text-left">Puesto</th>
                        <th className="p-3 text-right">Sueldo Base</th>
                        <th className="p-3 text-center">Comisiones</th>
                        <th className="p-3 text-center">AFP</th>
                        <th className="p-3 text-center">ARS</th>
                        <th className="p-3 text-center">CXC</th>
                        <th className="p-3 text-center">Seg. Médico</th>
                        <th className="p-3 text-right">A Pagar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {empleados.map((emp) => {
                        const pago = empleadosPago[emp.id] || {};
                        const sueldoBase = formData.tipo_pago === 'quincenal' ? emp.sueldo_quincenal : emp.sueldo_mensual;
                        const bruto = sueldoBase + (pago.comisiones || 0);
                        const deducciones = (pago.afp || 0) + (pago.ars || 0) + (pago.cxc || 0) + (pago.seg_medico || 0);
                        const neto = bruto - deducciones;
                        
                        return (
                          <tr
                            key={emp.id}
                            className={`border-b ${pago.selected ? '' : 'opacity-50 bg-muted/30'}`}
                            data-testid={`pago-emp-${emp.id}`}
                          >
                            <td className="p-3 text-center">
                              <Checkbox
                                checked={pago.selected}
                                onCheckedChange={(checked) => handleEmpleadoChange(emp.id, 'selected', checked)}
                              />
                            </td>
                            <td className="p-3">
                              <div>
                                <p className="font-medium">{emp.nombre_completo}</p>
                                <p className="text-xs text-muted-foreground">{emp.apodo || '-'}</p>
                              </div>
                            </td>
                            <td className="p-3 text-muted-foreground">{emp.puesto}</td>
                            <td className="p-3 text-right font-mono">{formatCurrency(sueldoBase)}</td>
                            <td className="p-3">
                              <FormattedInput
                                className="h-8 w-24 text-center"
                                value={pago.comisiones || 0}
                                onChange={(val) => handleEmpleadoChange(emp.id, 'comisiones', val)}
                                disabled={!pago.selected}
                              />
                            </td>
                            <td className="p-3">
                              <FormattedInput
                                className="h-8 w-20 text-center"
                                value={pago.afp || 0}
                                onChange={(val) => handleEmpleadoChange(emp.id, 'afp', val)}
                                disabled={!pago.selected}
                              />
                            </td>
                            <td className="p-3">
                              <FormattedInput
                                className="h-8 w-20 text-center"
                                value={pago.ars || 0}
                                onChange={(val) => handleEmpleadoChange(emp.id, 'ars', val)}
                                disabled={!pago.selected}
                              />
                            </td>
                            <td className="p-3">
                              <FormattedInput
                                className="h-8 w-20 text-center"
                                value={pago.cxc || 0}
                                onChange={(val) => handleEmpleadoChange(emp.id, 'cxc', val)}
                                disabled={!pago.selected}
                              />
                            </td>
                            <td className="p-3">
                              <FormattedInput
                                className="h-8 w-20 text-center"
                                value={pago.seg_medico || 0}
                                onChange={(val) => handleEmpleadoChange(emp.id, 'seg_medico', val)}
                                disabled={!pago.selected}
                              />
                            </td>
                            <td className="p-3 text-right font-bold font-mono text-[#E73425]">
                              {pago.selected ? formatCurrency(neto) : '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Totals Summary */}
            <Card className="border-border/60 bg-gradient-to-r from-red-50 to-orange-50">
              <CardContent className="pt-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div>
                    <p className="text-sm text-muted-foreground">Empleados</p>
                    <p className="text-2xl font-bold">{totals.empleadosSeleccionados}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Bruto</p>
                    <p className="text-2xl font-bold" style={{ fontFamily: 'JetBrains Mono' }}>
                      {formatCurrency(totals.totalBruto)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Deducciones</p>
                    <p className="text-2xl font-bold text-red-500" style={{ fontFamily: 'JetBrains Mono' }}>
                      -{formatCurrency(totals.totalDeducciones)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total a Pagar</p>
                    <p className="text-2xl font-bold text-[#E73425]" style={{ fontFamily: 'JetBrains Mono' }}>
                      {formatCurrency(totals.totalNeto)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Buttons */}
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/nomina')}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-[#E73425] hover:bg-[#D81C23]"
                disabled={loading || totals.empleadosSeleccionados === 0}
                data-testid="submit-pago"
              >
                {loading ? (
                  <div className="spinner h-5 w-5" />
                ) : (
                  <>
                    <Check size={18} className="mr-2" />
                    Registrar Pago de Nómina
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Layout>
  );
}
